import type React from "react";
import { useId } from "react";
import Popover from "../../app/Popover";
import NumberField from "../../primitives/NumberField";
import FieldLabel from "../FieldLabel";
import { FieldMessages, fieldAccessibility, type LegacyField } from "./LegacyFieldAdapter";

// Redux Form adapter for a percentage input, extracted from inputs.jsx as part
// of replacing react-numeric-input. inputs.jsx re-exports this so existing call
// sites are unchanged.
//
// The adapter owns every Redux Form concern: reading field.input, translating
// the primitive's value callback back into field.input.onChange, and rendering
// the label and messages. The primitive underneath knows none of that.

type PercentageFieldProps = LegacyField & {
  className?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  id?: string;
  input: {
    name?: string;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    onChange: (value: number) => void;
    onFocus?: React.FocusEventHandler<HTMLInputElement>;
    value?: number | string | null;
  };
  inputPlaceholder?: string;
  /** Overrides the Redux Form value for display. */
  inputValue?: number | string;
  /** Omit both bounds so values may be negative or greater than 100. */
  unbounded?: boolean;
  /** `null` omits the bound. Defaults to 100 unless `unbounded`. */
  max?: number | null;
  /** `null` omits the bound. Defaults to 0 unless `unbounded`. */
  min?: number | null;
  onInputChange?: (value: number) => void;
  placement?: string;
  placeholder?: string;
  popoverContent?: string;
  precision?: number;
  /** Defaults to `"any"` so decimal percentages are not rejected on submit. */
  step?: number | "any";
};

/** Redux Form yields "" for an empty field; the primitive's blank is null. */
function toNumberOrNull(value: number | string | null | undefined): number | null {
  if (value === "" || value === null || value === undefined) {
    return null;
  }
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  // Edit Model initializes these fields with formatPercentage strings ("12.34%").
  const normalized = value.replace(/,/g, "").replace(/%/g, "").trim();
  if (normalized === "") {
    return null;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
}

export default function PercentageField(field: PercentageFieldProps): React.ReactElement {
  const generatedId = useId();
  const inputId = field.id || field.input.name || generatedId;
  const accessibility = fieldAccessibility(field, inputId);
  const precision = field.precision || 2;

  // The previous implementation assigned to field.input.value during render,
  // mutating the Redux Form prop object as a side effect. The override is now
  // computed locally instead; the rendered result is identical.
  const value = toNumberOrNull(field.inputValue ?? field.input.value);

  const popover = field.popoverContent && <Popover content={field.popoverContent} placement={field.placement} />;

  return (
    <div className={accessibility.hasError ? "app-validation-error" : undefined}>
      {!field.hideLabel && (
        <FieldLabel htmlFor={inputId} popover={popover}>
          {field.placeholder}
        </FieldLabel>
      )}
      <NumberField
        aria-describedby={accessibility.inputProps["aria-describedby"]}
        aria-invalid={accessibility.inputProps["aria-invalid"]}
        aria-required={accessibility.inputProps["aria-required"]}
        className={field.className}
        disabled={field.disabled}
        id={inputId}
        // The visible label points at `inputId`, but the primitive puts that id
        // on the Base UI root rather than the input inside it, so `htmlFor`
        // never reaches the control and the field goes unnamed. Naming the
        // input directly restores it without touching the label a user sees --
        // which matters most in the model securities grid, where the labels are
        // hidden after the first row and there is nothing else to go on.
        label={field.placeholder}
        max={field.unbounded || field.max === null ? undefined : (field.max ?? 100)}
        min={field.unbounded || field.min === null ? undefined : (field.min ?? 0)}
        name={field.input.name}
        onBlur={field.input.onBlur}
        onFocus={field.input.onFocus}
        onValueChange={(next) => {
          // Clearing the field is not propagated, preserving the previous
          // behaviour where a null value left the stored value untouched.
          if (next === null) {
            return;
          }
          // No trimming here: the primitive already reports at `precision`.
          field.input.onChange(next);
          field.onInputChange?.(next);
        }}
        percent
        placeholder={field.inputPlaceholder || field.placeholder}
        precision={precision}
        step={field.step ?? "any"}
        value={value}
      />
      <FieldMessages accessibility={accessibility} field={field} />
    </div>
  );
}
